import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subscription } from 'rxjs';
import { CustomerServiceInsightsService } from 'src/app/features/customer-service-insights/services/customer-service-insights.service';
import { LocationDetails, CALL_REASON_MAP } from 'src/app/features/customer-service-insights/types/customer-types';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { ServiceInsightsExportPhotoService } from 'src/app/features/service-insights/services/service-insights-export-photo-service';

@Component({
    selector: 'ph-location-insights-details',
    templateUrl: './location-insights-details.component.html',
    styleUrls: ['./location-insights-details.component.scss'],
    standalone: false
})
export class LocationInsightsDetailsComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageTitle: string = 'Location Insights';
  byIssuesData: any = [];
  locationsData: LocationDetails[] = [];
  filteredLocationData: LocationDetails[] = [];
  isExportPhotoView: boolean = false;

  showTableLoader: boolean = true;
  noLocationsData: boolean = false;

  totalRows: number = 0;
  pageSize = 100;
  previousPageSize = 100;
  pageSizeOptions: number[] = [25, 50, 75, 100];

  isLoading: boolean = false;

  private issuesDataSubscription: Subscription = new Subscription();
  private locationDetailsDataSubscription: Subscription = new Subscription();

  constructor(
    private _customerServiceInsightsService: CustomerServiceInsightsService,
    private _mixpanelService: MixpanelService ,
    private _authenticationservice: AuthenticationService,
    private _exportPhotoService : ServiceInsightsExportPhotoService
  ) { }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.subscribeToLocationDetailsData();
    this._exportPhotoService.isExportPhotoViewObervable.subscribe(value => this.isExportPhotoView = value);

  }

  ngOnDestroy(): void {
    this.issuesDataSubscription.unsubscribe();
    this.locationDetailsDataSubscription.unsubscribe();
  }

  private subscribeToLocationDetailsData() : void {
    this.isLoading = true;
    this.showTableLoader = true;
    this.issuesDataSubscription = this._customerServiceInsightsService.singleLocationIssuesCountData$.subscribe(
      data => {
        this.byIssuesData = data;
        this.isLoading = false;
      }
    )

  this.locationDetailsDataSubscription = this._customerServiceInsightsService.locationDetailsData$.subscribe(
    data => {
      this.showTableLoader = false;
      this.totalRows = data.totalRows;
      this.locationsData = data.locationData.map((location: LocationDetails) => {
        return {
          ...location,
          callReason: CALL_REASON_MAP[location.callReason] || location.callReason
        };
      });
      this.filteredLocationData = this.locationsData;
    }
  );
}

  onPageChange(event: PageEvent) {
    this.showTableLoader = true;
    if(this.previousPageSize !== event.pageSize) {
      this.paginator.pageIndex = 0;
      this.previousPageSize = event.pageSize;
    }
    this.updatePageData(this.paginator.pageIndex, event.pageSize);
  }

  updatePageData(pageIndex: number, pageSize: number) {
    this._customerServiceInsightsService.setPageNumber(pageIndex + 1);
    this._customerServiceInsightsService.setPageSize(pageSize);
  }

  handleTooltipClick(obsName: string) {
    const currentFilter = this._customerServiceInsightsService.getCurrentGraphDetailFilter();
    if (currentFilter !== obsName) {
      this.showTableLoader = true;
      this._customerServiceInsightsService.setGraphDetailFilter(obsName);
      this._customerServiceInsightsService.setPageNumber(1);
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this._mixpanelService.track(`PH bubble chart clicked`, {
        ImpersonatedCustomer: this._authenticationservice.customerName??" ",
        ImpersonatedUser:  this._authenticationservice.userName??" ",
        AccountName: this.filteredLocationData[0].accountName,
        AccountNumber: this.filteredLocationData[0].accountNumber,
      });
    } else {
      // If the same filter is clicked, we should hide the loader
      this.showTableLoader = false;
    }
  }

  async downloadWorkOrder(pdfBlobUrl: string, item: LocationDetails) {
    this.executeBrowserDownload(pdfBlobUrl);
    this._mixpanelService.track(`PH View Report button clicked`, {
      ImpersonatedCustomer: this._authenticationservice.customerName??" ",
      ImpersonatedUser:  this._authenticationservice.userName??" ",
      AccountName: item.accountName,
      CallType: item.callReason,
      CallDate: item.startDate,
      AccountNumber: item.accountNumber,
    });
  }
  openPhotoInNewTab(item: any) {
    this._mixpanelService.track('PH - Export Photo Gallery Opened',{
      ImpersonatedCustomer: this._authenticationservice.customerName??" ",
      ImpersonatedUser:  this._authenticationservice.userName??" ",
      AccountName: item.accountName,
      CallType: item.callReason,
      CallDate: item.startDate,
      AccountNumber: item.accountNumber,
    });
    this._exportPhotoService.setServiceInsightsPhotoObject({
      photoInfo: item?.photoInfo?.map((info : any,index: number) => {
        return {
          id: index,
          url : info?.photoBaseUrl ?? "",
          description: info?.photoDescription ?? "",
        }
      }) ?? [],
      shippingCity:item?.shippingCity ?? "",
      shippingState:item?.shippingState ?? "",
      accountName:item?.accountName ?? "",
      endDate:item?.endDate ?? "",
      ecolabRep: item?.ecolabRep ?? "",
      photoCardFooter: `${this.formatDate(item?.endDate)} | ${item?.ecolabRep}/${item.accountName}`,
      workOrderId: item?.workOrderId ?? "",
      accountNumber: item?.accountNumber ?? "",
    });
    this._exportPhotoService.setIsExportPhotoView(true)
  }

  private formatDate(date: string) : string {
    let dateObj = new Date(date);
    let dateInGMT = new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * 60000);
    return dateInGMT.toLocaleDateString('en-US', {month: "long", day: "numeric", year: "numeric"});
  }

  private executeBrowserDownload(url: string): void {
    const xhr = new XMLHttpRequest();
    xhr.responseType = 'blob';
    xhr.onload = () => { this.handlePdfBlobResponse(xhr.response); };
    xhr.open('GET', url, true);
    xhr.send();
  }

  private handlePdfBlobResponse(blobData: unknown): void {
    const blob = new Blob([blobData as BlobPart], { type: "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);
    if (blobUrl && blobUrl.startsWith('blob:')) {
      window.open(blobUrl);
    }
  }

}

